import React from "react";
import { MONO, shellAntdTheme, STATUS } from "../../shellTokens";

/**
 * Pieces shared by the create and edit modes of the order form.
 *
 * Both modes render the same OrderFormPage; this module holds the parts the
 * page, its three section cards and the readiness rail all need, so none of
 * them is duplicated per mode.
 */

/**
 * The order screens use the ৳ symbol for line-item density; the Overview and
 * Teams dashboards use the "BDT" prefix at their lower number count. Divergent
 * on purpose — `symbol` is the knob rather than a second formatter.
 */
export const fmtBDT = (n, { symbol = "৳" } = {}) =>
  `${symbol}${symbol === "৳" ? "" : " "}${Math.round(Number(n) || 0).toLocaleString("en-US")}`;

/** Turns an antd error path into something an operator can read. */
export const errorLabel = (name) => {
  if (Array.isArray(name) && name[0] === "products") {
    return `Product ${Number(name[1]) + 1} — ${name[2]}`;
  }
  return Array.isArray(name) ? name.join(" › ") : String(name);
};

export const isFilled = (v) =>
  v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);

/** Fields the backend requires on every product line, in tab order. */
/**
 * What a product line must have before the order can be submitted — mirrors
 * StoreOrderRequest. Description and fabric pattern are deliberately absent:
 * both are optional there, and counting them here would leave the readiness
 * meter short of full on a line the API would happily accept.
 */
export const PRODUCT_FIELDS = [
  "productType",
  "productFormation",
  "fabrics",
  "fabricType",
  "quantity",
  "price",
];

export const PRODUCT_FIELD_LABELS = {
  productType: "product type",
  productFormation: "product formation",
  fabrics: "fabric colour",
  fabricType: "fabric type",
  quantity: "quantity",
  price: "unit price",
  productDescription: "description",
};

/** Required customer fields for a channel — mirrors StoreOrderRequest. */
export const customerFields = (channel) =>
  channel === "offline"
    ? ["phone", "name", "facebookId", "address", "division", "district", "upazila"]
    : ["phone", "name", "facebookId", "altPhone", "address", "division", "district", "upazila", "marketplace"];

export const DELIVERY_FIELDS = ["deliveryChannel", "deliveryDate", "deliveryCharge"];

/**
 * Per-section required/filled counts, feeding the readiness rail. Sections are
 * the three cards on the page, so a count in the rail always points at one card.
 */
export const readiness = (values, channel) => {
  const products = values.products || [];
  const productTotal = Math.max(products.length, 1) * PRODUCT_FIELDS.length;
  const productDone = products.reduce(
    (n, p) => n + PRODUCT_FIELDS.filter((f) => isFilled(p?.[f])).length,
    0,
  );

  const custFields = customerFields(channel);
  const custDone = custFields.filter((f) => isFilled(values[f])).length;
  const delDone = DELIVERY_FIELDS.filter((f) => isFilled(values[f])).length;

  const sections = [
    { key: "products", label: "Products", done: productDone, total: productTotal },
    { key: "customer", label: "Customer", done: custDone, total: custFields.length },
    { key: "delivery", label: "Delivery", done: delDone, total: DELIVERY_FIELDS.length },
  ];

  const done = sections.reduce((n, s) => n + s.done, 0);
  const total = sections.reduce((n, s) => n + s.total, 0);
  return { sections, done, total, percent: total ? Math.round((done / total) * 100) : 0 };
};

/** Which of the three sections an antd error path belongs to. */
export const sectionOfField = (name) => {
  const head = Array.isArray(name) ? name[0] : name;
  if (head === "products") return "products";
  if (DELIVERY_FIELDS.includes(head)) return "delivery";
  return "customer";
};

export const dotColor = ({ errors, done, total }) => {
  if (errors) return STATUS.error;
  if (done >= total) return STATUS.ok;
  if (done > 0) return STATUS.warn;
  return "var(--dim)";
};

/** The shell's antd theme; shared with the other redesigned screens. */
export const orderFormTheme = shellAntdTheme;

/* ---- Presentational primitives ---------------------------------------- */

export const SectionCard = ({ id, title, subtitle, action, children, bodyStyle }) => (
  <section
    id={id}
    style={{
      background: "var(--surface)",
      border: "1px solid var(--border)",
      borderRadius: 14,
      overflow: "hidden",
    }}
  >
    <header
      className="et-of-cardhead"
      style={{
        padding: "14px 18px",
        borderBottom: "1px solid var(--border)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
      }}
    >
      <div>
        <div style={{ font: "700 14.5px Outfit, sans-serif" }}>{title}</div>
        {subtitle && (
          <div style={{ font: "400 12px Outfit, sans-serif", color: "var(--muted)", marginTop: 2 }}>
            {subtitle}
          </div>
        )}
      </div>
      {action}
    </header>
    <div style={{ padding: 18, ...bodyStyle }}>{children}</div>
  </section>
);

/** One labelled band inside a card, separated by a rule from the next. */
export const Band = ({ label, children, last }) => (
  <div className="et-of-band" style={{ borderBottom: last ? "none" : "1px solid var(--border)" }}>
    <div className="et-of-band__label">{label}</div>
    <div className="et-of-band__body">{children}</div>
  </div>
);

export const Mono = ({ children, style, title }) => (
  <span title={title} style={{ fontFamily: MONO, fontVariantNumeric: "tabular-nums", ...style }}>{children}</span>
);

export const Dot = ({ color, size = 7 }) => (
  <span
    style={{
      flex: "none",
      width: size,
      height: size,
      borderRadius: size,
      background: color,
      display: "inline-block",
    }}
  />
);
